import type { APInvoice, SearchDocumentResult } from '@erp/rpc';
import { APInvoiceStatus } from '@erp/shared-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { InvoiceStatusBadge } from '@/components/accounts-payable/shared/InvoiceStatusBadge';
import {
  FileTextIcon,
  BuildingsIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  WarningIcon,
  CheckCircleIcon,
  XCircleIcon,
  CubeIcon,
  LinkIcon,
  PlusIcon,
  XIcon,
  InfoIcon,
  ClockIcon,
  PencilIcon,
  CheckIcon,
} from '@phosphor-icons/react';
import {
  AlertContainer as Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatDateOnlyString, parseDateOnly } from '@/utils/dateUtils';
import { useMemo, useState } from 'react';
import {
  useCreateEntityAssociation,
  useDeleteEntityAssociation,
  useEntityAssociationsList,
} from '@/api/rpc/hooks/core/useEntityAssociations';
import { SystemEntity, EntityAssociationType } from '@erp/shared-types';
import { SystemEntityInline } from '@/components/system-entities';
import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';
import { DocumentAutocomplete } from '@/components/accounts-payable/DocumentAutocomplete';
import { AdvancedDocumentSearch } from '@/components/accounts-payable/AdvancedDocumentSearch';
import { useUpdateAPInvoice } from '@/api/rpc/hooks/accounts-payable/useUpdateAPInvoice';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { PillInput } from '@/components/common/pills/PillInput';
import { PillList } from '@/components/common/pills/PillList';

import { format } from 'date-fns';

// Helper function to format currency
const formatCurrencyValue = (
  value: string | undefined | null,
  currency = 'USD',
): string => {
  if (!value) return 'N/A';

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

// Helper function to generate potential errors
const generatePotentialErrors = (
  invoice: APInvoice,
): { type: 'error' | 'warning'; message: string }[] => {
  const issues: { type: 'error' | 'warning'; message: string }[] = [];

  // Check for missing required fields (errors)
  if (!invoice.invoiceNumber) {
    issues.push({ type: 'error', message: 'Missing invoice number' });
  }

  const totalAmount = Number(invoice.totalAmount);
  if (!invoice.totalAmount || Number.isNaN(totalAmount) || totalAmount <= 0) {
    issues.push({ type: 'error', message: 'Invalid total amount' });
  }

  // Check for voided invoices
  if (invoice.isVoided) {
    issues.push({ type: 'warning', message: 'Invoice has been voided' });
  }

  // Check for payment hold
  if (invoice.paymentHold) {
    issues.push({
      type: 'warning',
      message: `Payment on hold${
        invoice.paymentHoldReason ? `: ${invoice.paymentHoldReason}` : ''
      }`,
    });
  }

  return issues;
};

export interface DerivedAssociation {
  entityType: SystemEntity;
  entityId: string;
}

interface InvoiceMetadataProps {
  invoice: APInvoice;
  derivedAssociations?: DerivedAssociation[];
  lineItemMatchSummary?: { total: number; matched: number };
}

export function InvoiceMetadata({
  invoice,
  derivedAssociations = [],
  lineItemMatchSummary,
}: InvoiceMetadataProps) {
  const issues = generatePotentialErrors(invoice);
  const errors = issues.filter((issue) => issue.type === 'error');
  const warnings = issues.filter((issue) => issue.type === 'warning');
  const updateInvoice = useUpdateAPInvoice();

  // Fetch associated POs
  const poAssociationsQuery = useEntityAssociationsList({
    entityId: invoice.id,
    entityType: SystemEntity.AP_INVOICE,
    associationType: EntityAssociationType.AP_INVOICE_MATCHING,
  });

  const apAssociations = useMemo(
    () => poAssociationsQuery.data?.associations || [],
    [poAssociationsQuery.data?.associations],
  );
  const createAssociation = useCreateEntityAssociation();
  const deleteAssociation = useDeleteEntityAssociation();

  // Get IDs of associated documents (POs, SOs, and Shipments)
  const associatedDocumentIds = useMemo(
    () =>
      apAssociations
        .filter(
          (assoc) =>
            assoc.entity.entityType === SystemEntity.INTERNAL_PURCHASE_ORDER ||
            assoc.entity.entityType === SystemEntity.AR_SALES_ORDER ||
            assoc.entity.entityType === SystemEntity.SCM_SHIPMENT,
        )
        .map((assoc) => assoc.entity.entityId),
    [apAssociations],
  );

  // Allow editing for DRAFT and MATCHED invoices
  const isEditable =
    invoice.status === APInvoiceStatus.DRAFT ||
    invoice.status === APInvoiceStatus.MATCHED;

  const [isAddingDocument, setIsAddingDocument] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [isEditingReferenceIds, setIsEditingReferenceIds] = useState(false);

  const handleAssociateDocument = async (document: SearchDocumentResult) => {
    let targetEntityType: SystemEntity;
    switch (document.documentType) {
      case 'PURCHASE_ORDER':
        targetEntityType = SystemEntity.INTERNAL_PURCHASE_ORDER;
        break;
      case 'SALES_ORDER':
        targetEntityType = SystemEntity.AR_SALES_ORDER;
        break;
      case 'SHIPMENT':
        targetEntityType = SystemEntity.SCM_SHIPMENT;
        break;
    }

    await createAssociation.mutateAsync({
      sourceEntityType: SystemEntity.AP_INVOICE,
      sourceEntityId: invoice.id,
      targetEntityType,
      targetEntityId: document.id,
      associationType: EntityAssociationType.AP_INVOICE_MATCHING,
    });
  };

  const handleRemoveDocumentAssociation = async (
    entityId: string,
    entityType: SystemEntity,
  ) => {
    await deleteAssociation.mutateAsync({
      sourceEntityType: SystemEntity.AP_INVOICE,
      sourceEntityId: invoice.id,
      targetEntityType: entityType,
      targetEntityId: entityId,
      associationType: EntityAssociationType.AP_INVOICE_MATCHING,
    });
  };

  // Check if scheduled date is tentative (not finalized)
  const isScheduledDateTentative =
    invoice.scheduledPaymentDate &&
    invoice.status !== APInvoiceStatus.FINALIZED;

  const handleSupplierChange = (supplierId: string | undefined) => {
    updateInvoice.mutate({
      id: invoice.id,
      supplierId: supplierId || null,
    });
  };

  const handleReferenceIdsUpdate = (referenceIds: string[]) => {
    updateInvoice.mutate({
      id: invoice.id,
      referenceIds: referenceIds.length > 0 ? referenceIds : null,
    });
  };
  const handleInvoiceDateChange = (date: Date | undefined) => {
    if (date) {
      updateInvoice.mutate({
        id: invoice.id,
        invoiceDate: format(date, 'yyyy-MM-dd'),
      });
    }
  };

  const handleDueDateChange = (date: Date | undefined) => {
    updateInvoice.mutate({
      id: invoice.id,
      dueDate: date ? format(date, 'yyyy-MM-dd') : null,
    });
  };

  const handlePreferredPayDateChange = (date: Date | undefined) => {
    updateInvoice.mutate({
      id: invoice.id,
      preferredPayDate: date ? format(date, 'yyyy-MM-dd') : null,
    });
  };

  const handleScheduledPaymentDateChange = (date: Date | undefined) => {
    updateInvoice.mutate({
      id: invoice.id,
      scheduledPaymentDate: date ? date.toISOString() : null,
      scheduledPaymentExplanation: date ? 'Manually set by user' : null,
    });
  };

  // Parse date strings to Date objects for the DatePicker
  // DATEONLY fields (invoiceDate, dueDate, preferredPayDate) use parseDateOnly
  // DATE/timestamp fields (scheduledPaymentDate) use Date constructor
  const invoiceDateValue = parseDateOnly(invoice.invoiceDate) ?? undefined;
  const dueDateValue = invoice.dueDate
    ? (parseDateOnly(invoice.dueDate) ?? undefined)
    : undefined;
  const preferredPayDateValue = invoice.preferredPayDate
    ? (parseDateOnly(invoice.preferredPayDate) ?? undefined)
    : undefined;
  // scheduledPaymentDate is a full ISO timestamp, not DATEONLY
  const scheduledPaymentDateValue = invoice.scheduledPaymentDate
    ? new Date(invoice.scheduledPaymentDate)
    : undefined;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileTextIcon className="h-4 w-4" />
            {invoice.invoiceNumber || 'No Invoice Number'}
          </CardTitle>
          <InvoiceStatusBadge
            invoice={invoice}
            lineItemMatchSummary={lineItemMatchSummary}
          />
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {(apAssociations.length > 0 ||
          derivedAssociations.length > 0 ||
          isEditable) && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <LinkIcon className="h-3 w-3" />
              Associated Documents
            </div>

            <div className="flex flex-wrap gap-1.5">
              {/* Explicit associations */}
              {apAssociations.map((assoc) => {
                const isRemovable =
                  assoc.entity.entityType ===
                    SystemEntity.INTERNAL_PURCHASE_ORDER ||
                  assoc.entity.entityType === SystemEntity.AR_SALES_ORDER ||
                  assoc.entity.entityType === SystemEntity.SCM_SHIPMENT;

                return (
                  <div
                    key={`${assoc.entity.entityType}:${assoc.entity.entityId}`}
                    className="flex items-center gap-1"
                  >
                    <SystemEntityInline
                      entityType={assoc.entity.entityType}
                      entityId={assoc.entity.entityId}
                      className="text-xs"
                    />
                    {isEditable && isRemovable && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={deleteAssociation.isPending}
                        onClick={() => {
                          if (deleteAssociation.isPending) return;
                          void handleRemoveDocumentAssociation(
                            assoc.entity.entityId,
                            assoc.entity.entityType,
                          );
                        }}
                        aria-label="Remove document association"
                      >
                        <XIcon className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                );
              })}
              {/* Derived associations from charge matches */}
              {derivedAssociations.map((derived) => (
                <div
                  key={`derived:${derived.entityType}:${derived.entityId}`}
                  className="flex items-center gap-1"
                >
                  <SystemEntityInline
                    entityType={derived.entityType}
                    entityId={derived.entityId}
                    className="text-xs"
                  />
                  <span className="text-[10px] text-muted-foreground italic">
                    (via charges)
                  </span>
                </div>
              ))}

              {isEditable && (
                <div className="flex items-center gap-1.5">
                  {isAddingDocument && (
                    <DocumentAutocomplete
                      invoiceId={invoice.id}
                      supplierId={invoice.supplierId || undefined}
                      invoiceDate={invoice.invoiceDate}
                      invoiceAmount={invoice.totalAmount}
                      currency={invoice.currency || 'USD'}
                      excludeDocumentIds={associatedDocumentIds}
                      onSelect={(document) => {
                        if (createAssociation.isPending) return;
                        setIsAddingDocument(false);
                        void handleAssociateDocument(document);
                      }}
                      onAdvancedSearch={() => setShowAdvancedSearch(true)}
                      className="max-w-md"
                    />
                  )}

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={createAssociation.isPending}
                    onClick={() => setIsAddingDocument((v) => !v)}
                    aria-label="Associate document"
                  >
                    {isAddingDocument ? (
                      <XIcon className="h-4 w-4" />
                    ) : (
                      <PlusIcon className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Advanced Document Search Modal */}
            <AdvancedDocumentSearch
              open={showAdvancedSearch}
              onOpenChange={setShowAdvancedSearch}
              invoiceId={invoice.id}
              supplierId={invoice.supplierId || undefined}
              invoiceDate={invoice.invoiceDate}
              invoiceAmount={invoice.totalAmount}
              currency={invoice.currency || 'USD'}
              excludeDocumentIds={associatedDocumentIds}
              onSelect={(document) => {
                setIsAddingDocument(false);
                void handleAssociateDocument(document);
              }}
            />
          </div>
        )}

        {/* Main Invoice Details */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <BuildingsIcon className="h-3 w-3" />
              Supplier
            </div>
            {isEditable ? (
              <SupplierAutocomplete
                supplierId={invoice.supplierId || undefined}
                onSelect={handleSupplierChange}
                className="w-full"
              />
            ) : invoice.supplierId ? (
              <SystemEntityInline
                entityType={SystemEntity.AP_SUPPLIER}
                entityId={invoice.supplierId}
              />
            ) : (
              <div className="text-xs text-muted-foreground">-</div>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <FileTextIcon className="h-3 w-3" />
                Reference IDs
              </div>
              {isEditable && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => setIsEditingReferenceIds((v) => !v)}
                  aria-label={
                    isEditingReferenceIds
                      ? 'Done editing reference IDs'
                      : 'Edit reference IDs'
                  }
                >
                  {isEditingReferenceIds ? (
                    <CheckIcon className="h-3 w-3" />
                  ) : (
                    <PencilIcon className="h-3 w-3" />
                  )}
                </Button>
              )}
            </div>
            {isEditable && isEditingReferenceIds ? (
              <PillInput
                values={invoice.referenceIds ?? []}
                onChange={handleReferenceIdsUpdate}
                placeholder="Add reference ID..."
              />
            ) : invoice.referenceIds && invoice.referenceIds.length > 0 ? (
              <PillList values={invoice.referenceIds} />
            ) : (
              <div className="text-sm text-muted-foreground">—</div>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CurrencyDollarIcon className="h-3 w-3" />
              Total Amount
            </div>
            <div className="text-lg font-bold text-green-600">
              {formatCurrencyValue(invoice.totalAmount, invoice.currency)}
            </div>
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              Invoice Date
            </div>
            {isEditable ? (
              <DatePicker
                value={invoiceDateValue}
                onSelect={handleInvoiceDateChange}
                placeholder="Select date"
              />
            ) : (
              <div className="font-medium text-sm">
                {formatDateOnlyString(invoice.invoiceDate)}
              </div>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              Due Date
            </div>
            {isEditable ? (
              <DatePicker
                value={dueDateValue}
                onSelect={handleDueDateChange}
                placeholder="Select due date"
              />
            ) : (
              <div className="font-medium text-sm">
                {invoice.dueDate
                  ? formatDateOnlyString(invoice.dueDate)
                  : 'Not specified'}
              </div>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              Preferred Pay Date
            </div>
            {isEditable ? (
              <DatePicker
                value={preferredPayDateValue}
                onSelect={handlePreferredPayDateChange}
                placeholder="Select preferred date"
              />
            ) : (
              <div className="font-medium text-sm">
                {invoice.preferredPayDate
                  ? formatDateOnlyString(invoice.preferredPayDate)
                  : 'Not specified'}
              </div>
            )}
          </div>

          <div className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <CalendarIcon className="h-3 w-3" />
              Scheduled Pay Date
              {isScheduledDateTentative && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1 text-amber-600">
                      <ClockIcon className="h-3 w-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="font-medium mb-1">Tentative</p>
                    <p>
                      This date will be confirmed when the invoice is approved.
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
              {invoice.scheduledPaymentExplanation && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex cursor-help">
                      <InfoIcon className="h-3 w-3 text-muted-foreground" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-sm">
                    <p className="font-medium mb-1">Calculation Details</p>
                    <pre className="whitespace-pre-wrap text-xs">
                      {invoice.scheduledPaymentExplanation}
                    </pre>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            {isEditable ? (
              <div className="flex items-center gap-1">
                <DatePicker
                  value={scheduledPaymentDateValue}
                  onSelect={handleScheduledPaymentDateChange}
                  placeholder="Select scheduled date"
                />
                {isScheduledDateTentative && (
                  <Badge
                    variant="outline"
                    className="text-xs text-amber-600 border-amber-300 bg-amber-50"
                  >
                    Tentative
                  </Badge>
                )}
              </div>
            ) : (
              <div className="font-medium text-sm flex items-center gap-1">
                {invoice.scheduledPaymentDate
                  ? formatDateOnlyString(invoice.scheduledPaymentDate)
                  : 'Not scheduled'}
              </div>
            )}
          </div>
        </div>

        {/* Error and Warning Display */}
        {(errors.length > 0 || warnings.length > 0) && (
          <div className="space-y-2">
            {errors.length > 0 && (
              <Alert className="border-red-200 bg-red-50 py-2">
                <XCircleIcon className="h-3 w-3 text-red-600" />
                <AlertDescription>
                  <div className="font-medium text-red-800 text-xs mb-1">
                    {errors.length} Error{errors.length > 1 ? 's' : ''} Found
                  </div>
                  <ul className="text-xs text-red-700 space-y-0.5">
                    {errors.map((error, index) => (
                      <li key={index} className="flex items-center gap-1.5">
                        <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                        {error.message}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {warnings.length > 0 && (
              <Alert className="border-amber-200 bg-amber-50 py-2">
                <WarningIcon className="h-3 w-3 text-amber-600" />
                <AlertDescription>
                  <div className="font-medium text-amber-800 text-xs mb-1">
                    {warnings.length} Warning{warnings.length > 1 ? 's' : ''}
                  </div>
                  <ul className="text-xs text-amber-700 space-y-0.5">
                    {warnings.map((warning, index) => (
                      <li key={index} className="flex items-center gap-1.5">
                        <span className="w-1 h-1 bg-amber-600 rounded-full"></span>
                        {warning.message}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {errors.length === 0 && warnings.length === 0 && (
              <Alert className="border-green-200 bg-green-50 py-2">
                <CheckCircleIcon className="h-3 w-3 text-green-600" />
                <AlertDescription>
                  <div className="font-medium text-green-800 text-xs">
                    No issues detected - Invoice is valid for processing
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Origin System Sync Status (e.g., Famous Voucher) */}
        {invoice.originSyncStatus &&
          invoice.originSystem === 'famous' &&
          invoice.originEntity === 'voucher' && (
            <div className="border-t pt-3 space-y-2">
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  Famous Voucher Status
                </div>
                <div className="flex items-center gap-2">
                  {invoice.originSyncStatus === 'success' ? (
                    <>
                      <CheckCircleIcon className="h-3 w-3 text-green-600" />
                      <Badge variant="outline" className="text-xs">
                        Created
                      </Badge>
                    </>
                  ) : invoice.originSyncStatus === 'pending' ? (
                    <>
                      <CubeIcon className="h-3 w-3 text-blue-600" />
                      <Badge variant="outline" className="text-xs">
                        Pending
                      </Badge>
                    </>
                  ) : invoice.originSyncStatus === 'failed' ? (
                    <>
                      <XCircleIcon className="h-3 w-3 text-red-600" />
                      <Badge variant="outline" className="text-xs">
                        Failed
                      </Badge>
                    </>
                  ) : null}
                </div>
              </div>
              {invoice.originId && (
                <div className="space-y-0.5">
                  <div className="text-xs font-medium text-muted-foreground">
                    Entry Number
                  </div>
                  <div className="text-xs font-mono">{invoice.originId}</div>
                </div>
              )}
              {invoice.originMetadata?.syncedAt &&
              typeof invoice.originMetadata.syncedAt === 'string' ? (
                <div className="space-y-0.5">
                  <div className="text-xs font-medium text-muted-foreground">
                    Synced At
                  </div>
                  <div className="text-xs">
                    {new Date(invoice.originMetadata.syncedAt).toLocaleString()}
                  </div>
                </div>
              ) : null}
              {invoice.originSyncErrorMessage && (
                <div className="space-y-0.5">
                  <div className="text-xs font-medium text-red-600">
                    Error Message
                  </div>
                  <div className="text-xs text-red-600">
                    {invoice.originSyncErrorMessage}
                  </div>
                </div>
              )}
            </div>
          )}

        {/* Additional Details */}
        {invoice.paymentTerms && (
          <div className="border-t pt-3 space-y-2">
            {invoice.paymentTerms && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  Payment Terms
                </div>
                <div className="text-xs">{invoice.paymentTerms}</div>
              </div>
            )}
            {/* invoice.notes && (
              <div className="space-y-0.5">
                <div className="text-xs font-medium text-muted-foreground">
                  Notes
                </div>
                <div className="text-xs">{invoice.notes}</div>
              </div>
            )*/}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
